import { useCallback } from "react";
import { useAuth0, type Auth0ContextInterface } from "@auth0/auth0-react";
import { loggerService } from "./observability";
import { sharedEnvironment } from "@akina/config";
import { useLocaleStore, type HomepageRedirectTarget, useHomepageRedirect } from "@akina/presentation";

const RECOVERABLE_AUTH_ERRORS = new Set([
  "consent_required",
  "login_required",
  "interaction_required",
  "invalid_grant",
  "missing_refresh_token",
]);

const isRecoverableAuthError = (error: unknown): error is { error: string } =>
  typeof error === "object" &&
  error !== null &&
  "error" in error &&
  typeof error.error === "string" &&
  RECOVERABLE_AUTH_ERRORS.has(error.error);

export enum Tenant {
  Portal = "PORTAL",
  Care = "CARE",
}

export type AuthAppState = {
  returnTo: string;
};

const LOGOUT_PARAMS = {
  logoutParams: {
    returnTo: sharedEnvironment.auth0RedirectUri,
  },
};

const logoutCare = async (
  redirectTarget: HomepageRedirectTarget | null,
  setLogoutRedirectTarget: (target: HomepageRedirectTarget | null) => void,
  logout: ({ logoutParams }: { logoutParams: { returnTo: string } }) => Promise<void>,
) => {
  setLogoutRedirectTarget(redirectTarget);
  loggerService.info("logout, redirect target:", redirectTarget);
  // wait to allow the redirect target to be set before logging out
  await new Promise(resolve => setTimeout(resolve, 300));
  await logout(LOGOUT_PARAMS);
};

export const useAuth = () => {
  const { isAuthenticated, isLoading, user, getAccessTokenSilently, loginWithRedirect, logout } = useAuth0();
  const { locale } = useLocaleStore();
  const setLogoutRedirectTarget = useHomepageRedirect(state => state.setTarget);

  const getAccessToken = useCallback(
    async (...args: Parameters<Auth0ContextInterface["getAccessTokenSilently"]>) => {
      try {
        return await getAccessTokenSilently(...args);
      } catch (error) {
        if (!isRecoverableAuthError(error)) throw error;

        loggerService.warn("[auth] Recoverable token error, prompting re-authentication", {
          error: error.error,
        });

        const { pathname, search, hash } = globalThis.window?.location ?? {};
        const returnTo = pathname ? `${pathname}${search}${hash}` : "/";

        const silentOptions = args[0];
        await loginWithRedirect({
          appState: { returnTo },
          authorizationParams: {
            ...silentOptions?.authorizationParams,
            prompt: error.error === "consent_required" ? "consent" : "login",
          },
        });

        throw error;
      }
    },
    [getAccessTokenSilently, loginWithRedirect],
  ) as Auth0ContextInterface["getAccessTokenSilently"];

  const authenticate = (type: "signup" | "login") => (appState: AuthAppState, email?: string, os?: string) =>
    loginWithRedirect({
      appState: appState,
      authorizationParams: {
        login_hint: email,
        screen_hint: type,
        ui_locales: locale,
        os: os,
      },
    });

  const refreshToken = async (route: string) => {
    await loginWithRedirect({
      appState: { returnTo: route },
    });
  };

  const appName = sharedEnvironment.appName;

  return {
    isAuthenticated,
    isLoading,
    user,
    getAccessTokenSilently: getAccessToken,
    loginWithRedirect: authenticate("login"),
    refreshToken,
    logout: (
      { redirectTarget }: { redirectTarget: HomepageRedirectTarget | null } = {
        redirectTarget: null,
      },
    ) =>
      appName === "portal" || appName === "webshop"
        ? logout(LOGOUT_PARAMS)
        : logoutCare(redirectTarget, setLogoutRedirectTarget, logout),
    registerWithRedirect: authenticate("signup"),
  };
};
